import { HttpMethod } from '@libs/core';

import type {
    FailedSkusOptions,
    FailedSkusResponse,
    JobDetails,
    JobsOptions,
    PromoEditorInterface,
    PromoJob,
    PromoJobIdResponse,
} from '@/types';

const prepareFormData = (values: PromoEditorInterface): FormData => {
    const formData = new FormData();

    formData.append('action', values.action);
    formData.append('promoName', values.promoName);

    if (values.action !== 'remove-all' && values.skusFile) {
        let file: Blob;
        const isFile = values.skusFile instanceof File;
        const isBlob = values.skusFile instanceof Blob;

        if (isBlob || isFile) {
            file = values.skusFile;
        } else {
            file = new Blob([], { type: 'text/csv' });
        }

        const fileName: string | undefined = 'name' in values.skusFile ? (values.skusFile.name as string) : undefined;

        formData.append('skusFile', file, fileName);
    }

    return formData;
};

export const fetchJobById = async (jobId: string): Promise<PromoJob | null> => {
    try {
        const response = await fetch(`/api/promoNameJobs/${jobId}`);

        if (!response.ok) {
            throw new Error(`Failed to fetch job ${jobId}`);
        }

        const { data } = await response.json();
        return data;
    } catch (err) {
        console.error(`Failed to fetch job ${jobId}`, err);
        return null;
    }
};

export const submitPromoOffer = async (values: PromoEditorInterface): Promise<PromoJobIdResponse | null> => {
    try {
        const formData = prepareFormData(values);

        const response = await fetch(`/api/promoNameJobs`, {
            method: HttpMethod.POST,
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`Failed to submit Promo Offer`);
        }

        const { data } = await response.json();
        return data;
    } catch (err) {
        console.error('Failed to submit Promo Offer', err);
        return null;
    }
};

export const fetchJobs = async (options: JobsOptions = {}): Promise<PromoJob[]> => {
    try {
        const { requestor, jobId, status, action } = options;

        const queryParams: Record<string, string> = {};

        if (requestor) {
            queryParams.requestor = requestor;
        }
        if (jobId) {
            queryParams.jobId = jobId;
        }
        if (status) {
            queryParams.status = status;
        }
        if (action) {
            queryParams.action = action;
        }

        let url = '/api/promoNameJobs';

        if (Object.keys(queryParams).length > 0) {
            const queryString = Object.entries(queryParams)
                .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
                .join('&');
            url += `?${queryString}`;
        }

        const response = await fetch(url);

        if (!response.ok) {
            console.error('Failed to fetch jobs', options);
            return [];
        }

        const { data } = await response.json();
        return data;
    } catch (err) {
        console.error('Error fetching jobs:', err);
        return [];
    }
};

export const fetchJobDetails = async (): Promise<JobDetails[]> => {
    try {
        const url = '/api/jobDetails';

        const response = await fetch(url);

        if (!response.ok) {
            console.error('Failed to fetch job details');
            return [];
        }

        const { data } = await response.json();
        return data;
    } catch (err) {
        console.error('Error fetching job details:', err);
        return [];
    }
};

export const fetchJobFailedSkus = async (
    jobId: string,
    options: FailedSkusOptions = {}
): Promise<FailedSkusResponse | null> => {
    try {
        const { pageNumber, pageSize } = options;

        const queryParams: Record<string, string> = {};

        if (pageNumber !== undefined) {
            queryParams.pageNumber = pageNumber.toString();
        }
        if (pageSize !== undefined) {
            queryParams.pageSize = pageSize.toString();
        }

        let url = `/api/promoNameJobs/${jobId}/failedSkus`;

        if (Object.keys(queryParams).length > 0) {
            const queryString = Object.entries(queryParams)
                .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
                .join('&');
            url += `?${queryString}`;
        }

        const response = await fetch(url);

        if (!response.ok) {
            throw new Error(`Failed to fetch failed SKUs for job ${jobId}`);
        }

        const { data } = await response.json();
        return data;
    } catch (err) {
        console.error(`Failed to fetch failed SKUs for job ${jobId}`, err);
        return null;
    }
};
